import React, { useState, useEffect, useMemo } from 'react';
import { Filter, Plus, Trash2, Play, AlertCircle, CheckCircle, GripVertical } from 'lucide-react';
import { ordemEntradaService } from '../../services/ordemEntradaService';
import { useToast } from '../../hooks/useToast';

// Extrai a parte numérica inicial do número do lote
const extrairNumero = (numero) => {
  if (!numero) return null;
  const m = String(numero).trim().match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

const OrdemEntradaFiltroNumeros = ({ leilaoId, organizadorId }) => {
  const toast = useToast();

  const [regras, setRegras] = useState([{ de: '', ate: '', prioridade: 1 }]);
  const [todosLotes, setTodosLotes] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (organizadorId) carregarRegras();
  }, [organizadorId]);

  useEffect(() => {
    if (leilaoId) carregarLotes();
  }, [leilaoId]);

  const carregarRegras = async () => {
    try {
      const regrasSalvas = await ordemEntradaService.listarFiltroNumeros(organizadorId, leilaoId);
      if (regrasSalvas.length > 0) {
        setRegras(regrasSalvas.map(r => ({ de: r.de, ate: r.ate, prioridade: r.prioridade })));
      }
    } catch {
      // silencioso
    }
  };

  const carregarLotes = async () => {
    try {
      const lotes = await ordemEntradaService.listarTodosLotes(leilaoId);
      setTodosLotes(lotes);
    } catch {
      // silencioso
    }
  };

  // ── Preview reativo ─────────────────────────────────────────────────────────
  const preview = useMemo(() => {
    const regrasFilled = regras.filter(r => r.de !== '' && r.ate !== '' && Number(r.de) <= Number(r.ate));
    const idsAlocados = new Set();
    const porFaixa = regrasFilled.map((regra) => {
      const grupo = todosLotes.filter(l => {
        const n = extrairNumero(l.numero);
        return n !== null && !idsAlocados.has(l.id) && n >= Number(regra.de) && n <= Number(regra.ate);
      });
      grupo.forEach(l => idsAlocados.add(l.id));
      return grupo;
    });
    const semFaixa = todosLotes.filter(l => !idsAlocados.has(l.id));
    return { porFaixa, semFaixa };
  }, [regras, todosLotes]);

  // ── Edição de regras ────────────────────────────────────────────────────────
  const adicionarFaixa = () => {
    setRegras(prev => [...prev, { de: '', ate: '', prioridade: prev.length + 1 }]);
    setResultado(null);
  };

  const removerFaixa = (index) => {
    setRegras(prev => prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, prioridade: i + 1 })));
    setResultado(null);
  };

  const atualizarFaixa = (index, campo, valor) => {
    setRegras(prev =>
      prev.map((r, i) => i === index ? { ...r, [campo]: valor === '' ? '' : Number(valor) } : r)
    );
    setResultado(null);
  };

  const moverFaixa = (index, direcao) => {
    const novas = [...regras];
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= novas.length) return;
    [novas[index], novas[alvo]] = [novas[alvo], novas[index]];
    setRegras(novas.map((r, i) => ({ ...r, prioridade: i + 1 })));
    setResultado(null);
  };

  const validar = () => {
    for (let i = 0; i < regras.length; i++) {
      const r = regras[i];
      if (r.de === '' || r.ate === '') return `Faixa ${i + 1}: preencha os dois campos`;
      if (Number(r.de) > Number(r.ate)) return `Faixa ${i + 1}: início não pode ser maior que fim`;
    }
    return null;
  };

  const aplicar = async () => {
    const erro = validar();
    if (erro) { toast.error(erro); return; }
    try {
      setSalvando(true);
      const regrasMapeadas = regras.map((r, i) => ({ de: Number(r.de), ate: Number(r.ate), prioridade: i + 1 }));
      const res = await ordemEntradaService.aplicarFiltroNumeros(organizadorId, leilaoId, regrasMapeadas);
      setResultado(res);
      toast.success(res.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao aplicar filtro');
    } finally {
      setSalvando(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Botões de ação */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => window.open(`/catalogo-leilao/${leilaoId}`, '_blank')}
          className="flex items-center gap-2 bg-white border-2 border-green-600 text-green-700 hover:bg-green-50 font-medium px-5 py-2.5 rounded-xl transition-all text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Catálogo Leilão
        </button>
        <button onClick={aplicar} disabled={salvando}
          className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium px-6 py-2.5 rounded-xl transition-all disabled:opacity-50 shadow-sm text-sm">
          <Play size={16} />
          {salvando ? 'Aplicando...' : 'Aplicar Filtro'}
        </button>
      </div>

      {/* Instrução */}
      <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex gap-3">
        <Filter size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-green-800">
          <p className="font-medium mb-1">Como funciona</p>
          <p>
            Defina faixas de números de lote e a ordem em que cada grupo entra no leilão.
            Lotes fora de qualquer faixa entram ao final, em ordem numérica.
          </p>
        </div>
      </div>

      {/* Regras */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Faixas numéricas</h2>
          <button
            onClick={adicionarFaixa}
            className="flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 px-4 py-2 rounded-lg transition-all"
          >
            <Plus size={16} />
            Adicionar faixa
          </button>
        </div>

        <div className="p-5 space-y-2">
          {regras.map((regra, index) => {
            const countFaixa = preview.porFaixa[index]?.length ?? null;
            const faixaValida = regra.de !== '' && regra.ate !== '' && Number(regra.de) <= Number(regra.ate);

            return (
              <div
                key={index}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5"
              >
                {/* Setas de reordenação */}
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moverFaixa(index, -1)} disabled={index === 0}
                    className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30 transition-all" title="Subir">
                    <GripVertical size={12} className="text-gray-400 rotate-90 scale-x-[-1]" />
                  </button>
                  <button onClick={() => moverFaixa(index, 1)} disabled={index === regras.length - 1}
                    className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30 transition-all" title="Descer">
                    <GripVertical size={12} className="text-gray-400 rotate-90" />
                  </button>
                </div>

                {/* Badge de prioridade */}
                <div className="w-7 h-7 rounded-lg bg-green-100 text-green-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>

                {/* Campos inline */}
                <span className="text-xs text-gray-500 font-medium">De</span>
                <input type="number" min="0" value={regra.de}
                  onChange={e => atualizarFaixa(index, 'de', e.target.value)}
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent text-center"
                  placeholder="0" />
                <span className="text-xs text-gray-500 font-medium">até</span>
                <input type="number" min="0" value={regra.ate}
                  onChange={e => atualizarFaixa(index, 'ate', e.target.value)}
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent text-center"
                  placeholder="999" />

                {/* Contador */}
                {faixaValida && countFaixa !== null && (
                  <span className={`text-xs font-medium px-2 py-1 rounded-md border ${
                    countFaixa === 0
                      ? 'text-amber-700 bg-amber-50 border-amber-200'
                      : 'text-green-700 bg-green-50 border-green-200'
                  }`}>
                    {countFaixa === 0 ? 'Nenhum' : `${countFaixa} lote${countFaixa !== 1 ? 's' : ''}`}
                  </span>
                )}

                <div className="flex-1" />

                {/* Remover */}
                {regras.length > 1 && (
                  <button onClick={() => removerFaixa(index)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
                    title="Remover faixa">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Lotes fora das faixas */}
        {preview.semFaixa.length > 0 && (
          <div className="mx-5 mb-5 border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200">
              <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
              <span className="text-sm font-medium text-amber-800">
                {preview.semFaixa.length} lote{preview.semFaixa.length !== 1 ? 's' : ''} fora das faixas — {preview.semFaixa.length !== 1 ? 'entrarão' : 'entrará'} ao final da ordem
              </span>
            </div>
            <div className="divide-y divide-amber-100 max-h-48 overflow-y-auto">
              {preview.semFaixa.map(lote => (
                <div key={lote.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-14 text-xs font-bold text-amber-700 bg-amber-100 rounded px-2 py-0.5 text-center flex-shrink-0">
                    {lote.numero}
                  </span>
                  <span className="text-sm text-amber-900 truncate flex-1">{lote.descricao}</span>
                  {lote.fazenda_nome && (
                    <span className="text-xs text-amber-600 truncate hidden sm:block">{lote.fazenda_nome}</span>
                  )}
                  {lote.qtd && (
                    <span className="text-xs text-amber-600 flex-shrink-0">{lote.qtd} cab</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mensagem quando todas as faixas estão cobertas */}
        {todosLotes.length > 0 && preview.semFaixa.length === 0 && regras.some(r => r.de !== '' && r.ate !== '') && (
          <div className="mx-5 mb-5 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <CheckCircle size={16} className="flex-shrink-0" />
            Todos os lotes estão cobertos pelas faixas definidas.
          </div>
        )}
      </div>

      {/* Resultado da última aplicação */}
      {resultado && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-4">
          <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
          <div className="text-sm text-green-800 flex-1">
            <p className="font-medium">{resultado.message}</p>
            {resultado.lotes_sem_faixa > 0 && (
              <p className="mt-1 text-green-700">
                {resultado.lotes_sem_faixa} lote{resultado.lotes_sem_faixa !== 1 ? 's' : ''} sem faixa → adicionado{resultado.lotes_sem_faixa !== 1 ? 's' : ''} ao final.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toast.toasts.map(t => (
          <div key={t.id}
            className={`px-6 py-4 rounded-xl shadow-xl text-white font-medium ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
            }`}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrdemEntradaFiltroNumeros;
